import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { fmtMoney } from "@/lib/api";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { TrendingUp, Plus, Trash2, Phone, MessageSquare, Users, CalendarCheck, FileSignature, Handshake, Trophy, DollarSign, Flame, Target, Pencil, X } from "lucide-react";

const LIME = "#C5E26A";

export const KPI_FIELDS = [
  { key: "calls_made", label: "Calls Made", icon: Phone, group: "marketing" },
  { key: "texts_sent", label: "Texts Sent", icon: MessageSquare, group: "marketing" },
  { key: "leads_generated", label: "Leads", icon: Users, group: "funnel" },
  { key: "appointments_set", label: "Appointments", icon: CalendarCheck, group: "funnel" },
  { key: "offers_made", label: "Offers Made", icon: FileSignature, group: "funnel" },
  { key: "contracts_signed", label: "Contracts", icon: Handshake, group: "funnel" },
  { key: "deals_closed", label: "Deals Closed", icon: Trophy, group: "outcome" },
  { key: "profit_earned", label: "Profit ($)", icon: DollarSign, group: "outcome" },
];

const PERIODS = [
  { id: 0, label: "All time" },
  { id: 30, label: "30 days" },
  { id: 7, label: "7 days" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Kpis() {
  const [period, setPeriod] = useState(0);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [streaks, setStreaks] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = period ? { days: period } : {};
      const [e, s, st] = await Promise.all([
        api.get("/kpis", { params }),
        api.get("/kpis/summary", { params }),
        api.get("/kpis/streaks"),
      ]);
      setEntries(e.data);
      setSummary(s.data);
      setStreaks(st.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  return (
    <div data-testid="kpis-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-tag mb-3"><span className="dot" /> Track your action</div>
          <h1 className="h-display text-4xl">My KPIs</h1>
          <p className="text-sm mt-3" style={{ color: "var(--text-muted)" }}>
            Wholesaling is a numbers game. Log your daily activity and watch the funnel work.
          </p>
        </div>
        <div className="flex gap-2" data-testid="kpi-period-tabs">
          {PERIODS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition"
              style={{
                background: period === p.id ? "var(--lime-glow)" : "var(--surface2)",
                color: period === p.id ? "var(--lime)" : "var(--text-muted)",
                border: period === p.id ? "1px solid var(--border-lime)" : "1px solid var(--border)",
              }}
              data-testid={`kpi-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !summary ? (
        <div className="py-20 flex justify-center"><span className="spinner" /></div>
      ) : (
        <>
          <StatGrid summary={summary} />
          <GoalsStreaks data={streaks} onSaved={load} />
          <div className="grid lg:grid-cols-5 gap-6 mt-6">
            <div className="lg:col-span-2">
              <LogForm onLogged={load} />
            </div>
            <div className="lg:col-span-3 space-y-6">
              <TrendChart entries={entries} />
              <FunnelChart totals={summary?.totals} />
            </div>
          </div>
          <EntriesTable entries={entries} onDeleted={load} />
        </>
      )}
    </div>
  );
}

function StatGrid({ summary }) {
  if (!summary) return null;
  const t = summary.totals || {};
  const auto = summary.auto || {};
  const manual = summary.manual || {};
  const d = summary.deal_stats || {};

  const Tile = ({ f }) => {
    const Icon = f.icon;
    const isMoney = f.key === "profit_earned";
    const a = auto[f.key] || 0;
    const m = manual[f.key] || 0;
    const total = t[f.key] || 0;
    const fmt = (v) => (isMoney ? fmtMoney(v) : v.toLocaleString());
    return (
      <div className="surface-card p-4" data-testid={`kpi-stat-${isMoney ? "profit" : f.key}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon size={13} style={{ color: "var(--lime)" }} />
          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{f.label}</span>
        </div>
        <div className="font-mono text-2xl" style={{ color: a > 0 && m === 0 ? "var(--lime)" : "var(--text)" }}>
          {fmt(total)}
        </div>
        {a > 0 && m > 0 && (
          <div className="font-mono text-[10px] mt-1" style={{ color: "var(--text-dim)" }} data-testid={`kpi-split-${f.key}`}>
            <span style={{ color: "var(--lime)" }}>{fmt(a)} tracked</span> + {fmt(m)} logged
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="kpi-stat-grid">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {KPI_FIELDS.map((f) => <Tile key={f.key} f={f} />)}
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        <div className="surface-card px-4 py-3 flex items-center gap-3" data-testid="kpi-stat-deals-submitted">
          <TrendingUp size={14} style={{ color: "var(--lime)" }} />
          <span className="text-sm" style={{ color: "var(--text-muted)" }}>
            From your pipeline: <b style={{ color: "var(--lime)" }}>{auto.leads_generated || 0}</b> leads ·{" "}
            <b style={{ color: "var(--text)" }}>{d.submitted || 0}</b> deals submitted ·{" "}
            <b style={{ color: "var(--lime)" }}>{d.closed || 0}</b> closed
          </span>
        </div>
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--text-dim)" }}>
        Numbers in green are counted by the portal from your leads and deals. Calls and texts you log yourself stack on top.
      </p>
    </div>
  );
}

function GoalsStreaks({ data, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  if (!data) return null;
  const goals = data.goals || {};
  const progress = data.week_progress || {};
  const goalFields = KPI_FIELDS.filter((f) => goals[f.key] > 0);

  const startEdit = () => {
    setVals(Object.fromEntries(KPI_FIELDS.map((f) => [f.key, goals[f.key] ? String(goals[f.key]) : ""])));
    setEditing(true);
  };

  const save = async () => {
    const out = {};
    for (const f of KPI_FIELDS) {
      const n = Number(vals[f.key]) || 0;
      if (n > 0) out[f.key] = n;
    }
    setSaving(true);
    try {
      await api.put("/kpis/goals", { goals: out });
      toast.success("Weekly goals saved");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save goals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface-card p-6 mt-6" data-testid="kpi-goals-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Target size={14} style={{ color: "var(--lime)" }} />
          <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
            Weekly goals & streaks
          </h2>
          <span className="font-mono text-[10px]" style={{ color: "var(--text-dim)" }}>
            · week of {data.week_start}
          </span>
        </div>
        {!editing && (
          <button onClick={startEdit} className="btn-ghost text-xs flex items-center gap-1.5" data-testid="kpi-goals-edit">
            <Pencil size={12} /> {goalFields.length ? "Edit goals" : "Set weekly goals"}
          </button>
        )}
      </div>

      {editing ? (
        <div data-testid="kpi-goals-form">
          <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
            Set a weekly target for the metrics you want to commit to. Leave blank to skip.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
            {KPI_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="field-label text-[11px]">{f.label} / wk</label>
                <input
                  inputMode="numeric"
                  className="input-base font-mono"
                  placeholder="—"
                  value={vals[f.key]}
                  onChange={(e) => setVals((s) => ({ ...s, [f.key]: e.target.value.replace(/[^\d.]/g, "") }))}
                  data-testid={`kpi-goal-input-${f.key}`}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="kpi-goals-save">
              {saving ? "Saving…" : "Save goals"}
            </button>
            <button onClick={() => setEditing(false)} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="kpi-goals-cancel">
              <X size={13} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="flex md:flex-col gap-4">
            <StreakBadge count={data.weekly_streak} unit="week" label="Goal streak" testid="kpi-weekly-streak" />
            <StreakBadge count={data.daily_streak} unit="day" label="Logging streak" testid="kpi-daily-streak" />
          </div>
          <div className="md:col-span-3">
            {goalFields.length === 0 ? (
              <div className="h-full flex items-center text-sm" style={{ color: "var(--text-dim)" }} data-testid="kpi-goals-empty">
                No weekly goals yet. Set targets like "100 calls/week" and build a streak hitting them.
              </div>
            ) : (
              <div className="space-y-4">
                {goalFields.map((f) => {
                  const cur = progress[f.key] || 0;
                  const target = goals[f.key];
                  const pct = Math.min((cur / target) * 100, 100);
                  const hit = cur >= target;
                  return (
                    <div key={f.key} data-testid={`kpi-goal-progress-${f.key}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium" style={{ color: "var(--text)" }}>{f.label}</span>
                        <span className="font-mono text-[11px]" style={{ color: hit ? "var(--success)" : "var(--text-muted)" }}>
                          {f.key === "profit_earned" ? `${fmtMoney(cur)} / ${fmtMoney(target)}` : `${cur.toLocaleString()} / ${target.toLocaleString()}`}
                          {hit && " ✓"}
                        </span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--surface2)" }}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${pct}%`, background: hit ? "var(--success)" : LIME, transition: "width 0.5s ease" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StreakBadge({ count, unit, label, testid }) {
  const lit = count > 0;
  return (
    <div
      className="flex items-center gap-3 px-4 py-3 rounded-xl flex-1"
      style={{
        background: lit ? "rgba(255,140,50,0.08)" : "var(--surface2)",
        border: lit ? "1px solid rgba(255,140,50,0.3)" : "1px solid var(--border)",
      }}
      data-testid={testid}
    >
      <Flame size={20} style={{ color: lit ? "#ff8c32" : "var(--text-dim)" }} fill={lit ? "#ff8c32" : "none"} />
      <div>
        <div className="font-mono text-xl leading-none" style={{ color: lit ? "#ff8c32" : "var(--text-dim)" }}>
          {count}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-wider mt-1" style={{ color: "var(--text-muted)" }}>
          {unit} {label}
        </div>
      </div>
    </div>
  );
}

function LogForm({ onLogged }) {
  const empty = Object.fromEntries(KPI_FIELDS.map((f) => [f.key, ""]));
  const [date, setDate] = useState(todayStr());
  const [vals, setVals] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v.replace(/[^\d.]/g, "") }));

  const submit = async () => {
    const payload = { date };
    let hasAny = false;
    for (const f of KPI_FIELDS) {
      const n = Number(vals[f.key]) || 0;
      payload[f.key] = n;
      if (n > 0) hasAny = true;
    }
    if (!hasAny) { toast.error("Enter at least one number to log"); return; }
    setSaving(true);
    try {
      await api.post("/kpis", payload);
      toast.success("Activity logged");
      setVals(empty);
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  const groups = [
    { id: "marketing", label: "Marketing Activity" },
    { id: "funnel", label: "Lead Funnel" },
    { id: "outcome", label: "Outcomes" },
  ];

  return (
    <div className="surface-card p-6" data-testid="kpi-log-form">
      <h2 className="font-mono text-xs uppercase tracking-wider mb-5" style={{ color: "var(--lime)" }}>Log activity</h2>
      <div className="mb-5">
        <label className="field-label">Date</label>
        <input
          type="date"
          className="input-base font-mono"
          value={date}
          max={todayStr()}
          onChange={(e) => setDate(e.target.value)}
          data-testid="kpi-date-input"
        />
      </div>
      {groups.map((g) => (
        <div key={g.id} className="mb-5">
          <div className="font-mono text-[10px] uppercase tracking-wider mb-2" style={{ color: "var(--text-dim)" }}>{g.label}</div>
          <div className="grid grid-cols-2 gap-3">
            {KPI_FIELDS.filter((f) => f.group === g.id).map((f) => (
              <div key={f.key}>
                <label className="field-label text-[11px]">{f.label}</label>
                <input
                  inputMode="numeric"
                  className="input-base font-mono"
                  placeholder="0"
                  value={vals[f.key]}
                  onChange={(e) => set(f.key, e.target.value)}
                  data-testid={`kpi-input-${f.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={submit} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="kpi-log-submit">
        <Plus size={15} /> {saving ? "Saving…" : "Add to my numbers"}
      </button>
      <p className="text-[11px] font-mono mt-3" style={{ color: "var(--text-dim)" }}>
        Logging the same date twice adds to that day's totals.
      </p>
    </div>
  );
}

const tooltipStyle = {
  background: "#141414",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  fontSize: 12,
  color: "#f5f5f5",
};
const tooltipItemStyle = { color: "#f5f5f5" };
const tooltipLabelStyle = { color: "#C5E26A" };

function TrendChart({ entries }) {
  const [metric, setMetric] = useState("calls_made");
  const data = useMemo(
    () => [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || "")).map((e) => ({
      date: (e.date || "").slice(5),
      value: e[metric] || 0,
    })),
    [entries, metric]
  );
  return (
    <div className="surface-card p-6" data-testid="kpi-trend-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Trend over time</h2>
        <select className="input-base py-1.5 text-xs w-auto" value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="kpi-trend-metric">
          {KPI_FIELDS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
      </div>
      {data.length === 0 ? (
        <div className="py-10 text-center text-sm" style={{ color: "var(--text-dim)" }}>No entries yet. Log your first day above.</div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="limeFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={LIME} stopOpacity={0.35} />
                <stop offset="100%" stopColor={LIME} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#777", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#777", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
            <Area type="monotone" dataKey="value" stroke={LIME} strokeWidth={2} fill="url(#limeFill)" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function FunnelChart({ totals }) {
  if (!totals) return null;
  const data = [
    { name: "Leads", value: totals.leads_generated || 0 },
    { name: "Appts", value: totals.appointments_set || 0 },
    { name: "Offers", value: totals.offers_made || 0 },
    { name: "Contracts", value: totals.contracts_signed || 0 },
    { name: "Closed", value: totals.deals_closed || 0 },
  ];
  return (
    <div className="surface-card p-6" data-testid="kpi-funnel-chart">
      <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>Deal funnel</h2>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey="name" tick={{ fill: "#777", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: "#777", fontSize: 10, fontFamily: "monospace" }} axisLine={false} tickLine={false} allowDecimals={false} />
          <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(197,226,106,0.06)" }} />
          <Bar dataKey="value" fill={LIME} radius={[4, 4, 0, 0]} maxBarSize={48} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function EntriesTable({ entries, onDeleted }) {
  const del = async (id) => {
    try {
      await api.delete(`/kpis/${id}`);
      toast.success("Entry deleted");
      onDeleted();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };
  if (entries.length === 0) return null;
  return (
    <div className="surface-card p-6 mt-6" data-testid="kpi-entries-table">
      <h2 className="font-mono text-xs uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>
        Daily log
        <span className="ml-2 normal-case tracking-normal" style={{ color: "var(--text-dim)" }}>
          numbers in green are verified from your connected tools
        </span>
      </h2>
      <div className="cards-on-phone space-y-3">
        {entries.map((e) => {
          const filled = KPI_FIELDS.filter((f) => (e[f.key] || 0) > 0);
          return (
            <div
              key={e.id}
              className="rounded-xl p-3"
              style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
              data-testid={`kpi-card-${e.date}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs" style={{ color: "var(--lime)" }}>{e.date}</span>
                <button
                  onClick={() => del(e.id)}
                  className="btn-ghost px-3"
                  style={{ minHeight: 44 }}
                  title="Delete entry"
                  data-testid={`kpi-delete-mobile-${e.date}`}
                >
                  <Trash2 size={14} style={{ color: "var(--red)" }} />
                </button>
              </div>
              {filled.length === 0 ? (
                <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>Nothing logged this day</p>
              ) : (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                  {filled.map((f) => (
                    <div key={f.key} className="flex items-baseline justify-between gap-2">
                      <span className="text-sm" style={{ color: "var(--text-muted)" }}>{f.label}</span>
                      <span className="font-mono text-sm">
                        {f.key === "profit_earned" ? fmtMoney(e[f.key] || 0) : e[f.key] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="overflow-x-auto table-on-desktop">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>
              <th className="py-2 pr-4">Date</th>
              {KPI_FIELDS.map((f) => <th key={f.key} className="py-2 pr-4 whitespace-nowrap">{f.label}</th>)}
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`kpi-entry-${e.date}`}>
                <td className="py-2.5 pr-4 font-mono text-xs" style={{ color: "var(--lime)" }}>{e.date}</td>
                {KPI_FIELDS.map((f) => {
                  const verified =
                    f.key === "calls_made"
                      ? (e.ghl_calls || 0) + (e.bd_calls || 0) || null
                      : f.key === "texts_sent"
                        ? e.ghl_texts || null
                        : null;
                  return (
                    <td key={f.key} className="py-2.5 pr-4 font-mono text-xs" style={{ color: (e[f.key] || 0) > 0 ? "var(--text)" : "var(--text-dim)" }}>
                      {f.key === "profit_earned" ? fmtMoney(e[f.key] || 0) : (e[f.key] || 0)}
                      {verified != null && (
                        <span
                          className="ml-1.5"
                          style={{ color: "var(--lime)" }}
                          title={`${verified} verified from your connected tools`}
                          data-testid={`kpi-verified-${f.key}-${e.date}`}
                        >
                          ({verified})
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="py-2.5 text-right">
                  <button onClick={() => del(e.id)} className="btn-ghost p-1.5" title="Delete entry" data-testid={`kpi-delete-${e.date}`}>
                    <Trash2 size={13} style={{ color: "var(--red)" }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
